import { SHIPPING_DOCUMENT_PARAM_KEY } from "./shippingDocumentDrawerParam";

interface DrawerOpenArgs {
  pathname: string;
  search: string;
  /** The manifest drawer's currently-open id, if any (a path segment). */
  currentManifestId: string | undefined;
}

/**
 * Drops the currently-open manifest id (and any deeper optional segment
 * after it, e.g. a line-item id) from the path — mirroring
 * `useSelectedIdInUrl`'s own truncation — so opening the bill-of-lading
 * drawer can never leave the manifest drawer's path segment behind.
 */
function pathWithoutManifestSegment(
  pathname: string,
  currentManifestId: string | undefined
): string[] {
  const segments = pathname.split("/").filter(Boolean);
  if (currentManifestId) {
    const idx = segments.lastIndexOf(currentManifestId);
    if (idx >= 0) {
      segments.length = idx;
    }
  }
  return segments;
}

/**
 * Builds the path+search for opening a manifest's drawer from `ManifestsV3`.
 * Always drops `?shippingDocumentId=` (the bill-of-lading drawer's own URL
 * state) so the two drawers can never both claim to be open.
 */
export function buildManifestOpenPath(args: DrawerOpenArgs & { manifestId: string }): string {
  const { pathname, search, currentManifestId, manifestId } = args;
  const segments = pathWithoutManifestSegment(pathname, currentManifestId);
  const params = new URLSearchParams(search);
  params.delete(SHIPPING_DOCUMENT_PARAM_KEY);
  const query = params.toString();
  return `/${[...segments, manifestId].join("/")}${query ? `?${query}` : ""}`;
}

/**
 * Builds the path+search for opening the bill-of-lading drawer from
 * `ManifestsV3`. Always drops the manifest drawer's path segment(s) so the
 * two drawers can never both claim to be open.
 */
export function buildBillOfLadingOpenPath(
  args: DrawerOpenArgs & { shippingDocumentId: string }
): string {
  const { pathname, search, currentManifestId, shippingDocumentId } = args;
  const segments = pathWithoutManifestSegment(pathname, currentManifestId);
  const params = new URLSearchParams(search);
  params.set(SHIPPING_DOCUMENT_PARAM_KEY, shippingDocumentId);
  return `/${segments.join("/")}?${params.toString()}`;
}

/** Builds the path+search for closing the bill-of-lading drawer: same path, `?shippingDocumentId=` removed. */
export function buildShippingDocumentClosePath(args: { pathname: string; search: string }): string {
  const { pathname, search } = args;
  const params = new URLSearchParams(search);
  params.delete(SHIPPING_DOCUMENT_PARAM_KEY);
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}`;
}

/** Reads the bill-of-lading drawer's id out of a location's search string. */
export function parseShippingDocumentIdParam(search: string): string | undefined {
  return new URLSearchParams(search).get(SHIPPING_DOCUMENT_PARAM_KEY) ?? undefined;
}
